import React, { useEffect, useRef, useState } from "react";
import {
  Bot,
  X,
  Send,
  Phone,
  Globe,
  IndianRupee,
  Briefcase,
  Info,
  Loader2,
} from "lucide-react";

function Chatbot() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const [messages, setMessages] = useState([
    {
      type: "bot",
      text: "Hello! \nHow can I help you today?\n\nनमस्ते! मैं आपकी किस प्रकार सहायता कर सकता हूँ?",
      time: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    },
  ]);

  const bottomRef = useRef(null);

  const quickQuestions = [
    {
      label: "Pricing",
      icon: IndianRupee,
      text: "What are your website development price plans?",
    },
    {
      label: "Services",
      icon: Globe,
      text: "What services does Web X Secure offer?",
    },
    {
      label: "Contact",
      icon: Phone,
      text: "How can I contact the Web X Secure team?",
    },
    {
      label: "Projects",
      icon: Briefcase,
      text: "Can you show me some of your recent projects?",
    },
    {
      label: "About",
      icon: Info,
      text: "Can you tell me more about Web X Secure?",
    },
  ];

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const getCurrentTime = () => {
    return new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const askBot = async (userText) => {
    if (!userText.trim() || loading) return;

    const userMessage = {
      type: "user",
      text: userText,
      time: getCurrentTime(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setMessage("");
    setLoading(true);

    try {
      const res = await fetch("https://goawin.xyz/api/chatbot/ask", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          prompt: userText,
        }),
      });

      const data = await res.json();

      const botMessage = {
        type: "bot",
        text:
          data?.answer ||
          "Sorry, I couldn't find that information right now.\n\nक्षमा करें, मुझे अभी यह जानकारी नहीं मिली।",
        time: getCurrentTime(),
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          type: "bot",
          text: "Something went wrong. Please try again.\n\nकुछ समस्या आ गई है। कृपया फिर से प्रयास करें।",
          time: getCurrentTime(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = () => {
    askBot(message);
  };

  return (
    <div className="fixed bottom-5 right-5 z-[999999]">
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="flex items-center gap-3 rounded-xl bg-primary pr-2  text-white shadow-xl hover:scale-105 transition"
        >
          <div className="relative h-12 w-12 rounded-xl bg-white flex items-center justify-center">
            <img
              src="https://webxsecure.in/myai.png"
              alt=""
              className="h-full w-full object-cover rounded-l-xl"
            />

            <span className="absolute bottom-1 right-1 h-2 w-2 rounded-full bg-green-500 border-2 border-white"></span>
          </div>
          <div className="text-left">
            <h4 className="text-sm font-bold">WXS AI Assistant</h4>
            <p className="text-xs"> Available 24/7 • Support</p>
          </div>
        </button>
      )}

      {open && (
        <div className="w-[380px] max-w-[94vw] h-[600px] max-h-[86vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col border">
          {/* Header */}
          <div className="bg-primary text-white px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-full bg-white p-1">
                <img
                  src="/myai.png"
                  alt="AI Assistant"
                  className="h-full w-full rounded-full object-cover"
                />
              </div>

              <div>
                <h3 className="font-bold text-base">Web X Secure Assistant</h3>
                <p className="text-xs text-white/80">Online • 24/7 Support</p>
              </div>
            </div>

            <button
              onClick={() => setOpen(false)}
              className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30"
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto bg-slate-50 px-3 py-4">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`mb-3 flex ${
                  msg.type === "user" ? "justify-end" : "justify-start"
                }`}
              >
                <div
                  className={`max-w-[82%] rounded-2xl px-4 py-3 text-sm whitespace-pre-line ${
                    msg.type === "user"
                      ? "bg-primary text-white rounded-tr-sm"
                      : "bg-white text-slate-800 border rounded-tl-sm shadow-sm"
                  }`}
                >
                  {msg.text}

                  <div
                    className={`text-[10px] mt-2 text-right ${
                      msg.type === "user" ? "text-white/70" : "text-slate-400"
                    }`}
                  >
                    {msg.time}
                  </div>
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start mb-3">
                <div className="bg-white border rounded-2xl rounded-tl-sm px-4 py-3 text-sm text-slate-600 shadow-sm flex items-center gap-2">
                  <Loader2 className="animate-spin text-primary" size={16} />
                  Typing...
                </div>
              </div>
            )}

            <div ref={bottomRef}></div>
          </div>

          {/* Quick Questions */}
          <div className="bg-white px-3 py-2 border-t">
            <div className="flex gap-2 flex-wrap pb-2">
              {quickQuestions.map((item, index) => {
                const Icon = item.icon;

                return (
                  <button
                    key={index}
                    onClick={() => askBot(item.text)}
                    disabled={loading}
                    className="flex items-center gap-1 shrink-0 rounded-full border px-2 py-1.5 text-xs text-slate-700 hover:bg-orange-50 hover:border-orange-300"
                  >
                    <Icon size={13} />
                    {item.label}
                  </button>
                );
              })}
            </div>

            {/* Input */}
            <div className="flex items-center gap-2">
              <input
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
                placeholder="Type your message..."
                className="flex-1 h-10 rounded-xl border px-4 text-sm outline-none focus:ring-2 focus:ring-orange-200"
              />

              <button
                onClick={handleSend}
                disabled={loading || !message.trim()}
                className="h-10 w-10 rounded-xl bg-primary text-white flex items-center justify-center hover:opacity-90 disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <Send size={18} />
                )}
              </button>
            </div>

            <p className="text-center text-[11px] text-slate-400 mt-2">
              Powered by{" "}
              <span className="text-primary font-semibold">Web X Secure</span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default Chatbot;
